<template>
  <div class="page-container reveal">
    <el-card class="main-list-card">
      <div class="list-header">
        <div class="header-content">
          <h2 class="page-title">Gestion des Catégories</h2>
          <p class="page-subtitle">{{ filteredCategories.length }} secteurs d'activité configurés</p>
        </div>
        <div class="header-actions">
          <el-input
            v-model="searchQ"
            placeholder="Rechercher une catégorie..."
            class="w-64"
          >
            <template #prefix><el-icon><Search /></el-icon></template>
          </el-input>
          
          <el-select v-model="statusFilter" class="w-36">
            <el-option label="Toutes" value="" />
            <el-option label="Actives" value="active" />
            <el-option label="Désactivées" value="inactive" />
          </el-select>

          <el-button type="primary" @click="openCreateModal" :icon="Plus">Nouvelle catégorie</el-button>
          <el-button @click="fetchCategories" :loading="isLoading" circle :icon="Refresh" />
        </div>
      </div>

      <div v-if="selectedRows.length" class="bulk-bar">
        <span class="bulk-count">{{ selectedRows.length }} sélectionné(s)</span>
        <el-button type="success" size="small" :loading="bulkLoading" @click="bulkToggleCategories(true)">Activer</el-button>
        <el-button type="danger" size="small" :loading="bulkLoading" @click="bulkToggleCategories(false)">Désactiver</el-button>
      </div>

      <el-table :data="filteredCategories" v-loading="isLoading" class="custom-table" stripe @selection-change="onSelectionChange">
        <el-table-column type="selection" width="45" />
        <el-table-column label="Icône" width="100" align="center">
          <template #default="{ row }">
            <div class="w-10 h-10 rounded-xl bg-white/5 border border-profissa-glass-border flex items-center justify-center text-2xl text-profissa-accent mx-auto">
              <span class="mdi" :class="'mdi-' + row.icon"></span>
            </div>
          </template>
        </el-table-column>
        
        <el-table-column label="Nom de la catégorie" min-width="200" sortable :sort-method="(a, b) => (a.label_fr || '').localeCompare(b.label_fr || '')">
          <template #default="{ row }">
            <div class="font-bold text-[0.95rem] text-profissa-text-main">{{ row.label_fr }}</div>
            <div class="text-xs font-mono text-profissa-text-dim mt-0.5">{{ row.key }}</div>
          </template>
        </el-table-column>

        <el-table-column label="Statut" width="150" sortable :sort-method="(a, b) => Number(a.is_active) - Number(b.is_active)">
          <template #default="{ row }">
            <div class="px-3 py-1 rounded-full text-[0.7rem] font-bold uppercase tracking-wide inline-block"
                 :class="row.is_active ? 'bg-green-500/10 text-green-500 border border-green-500/20' : 'bg-white/5 text-profissa-text-dim border border-profissa-glass-border'">
              {{ row.is_active ? 'Active' : 'Désactivée' }}
            </div>
          </template>
        </el-table-column>

        <el-table-column label="Actions" width="80" fixed="right" align="center">
          <template #default="{ row }">
            <ActionDropdown 
              :actions="getCategoryActions(row)" 
              @action="(k) => handleCategoryAction(k, row)" 
            />
          </template>
        </el-table-column>
      </el-table>

      <el-empty v-if="!isLoading && !filteredCategories.length" description="Aucune catégorie trouvée" />
    </el-card>

    <!-- Modal Creation / Edition -->
    <el-dialog append-to-body
      v-model="showModal"
      :title="editingCategory ? 'Modifier la catégorie' : 'Nouvelle catégorie'"
      width="500px"
      custom-class="glass-dialog"
    >
      <el-form label-position="top" class="premium-form">
        <el-form-item label="Nom de la catégorie (FR)" required>
          <el-input v-model="form.label_fr" placeholder="ex: Climatisation" @input="autoKey" />
        </el-form-item>

        <el-form-item label="Identifiant technique (Clé)" required>
          <el-input v-model="form.key" :disabled="!!editingCategory" placeholder="auto-généré" />
        </el-form-item>

        <el-form-item label="Sélecteur d'icône">
          <el-popover
            trigger="click"
            placement="bottom"
            width="400"
            popper-class="premium-icon-popover"
            v-model:visible="iconPopoverVisible"
          >
            <template #reference>
              <div class="flex items-center gap-4 p-4 bg-white/5 border border-profissa-glass-border rounded-xl cursor-pointer hover:border-profissa-accent transition-all" :class="{ '!border-profissa-accent shadow-glow': iconPopoverVisible }">
                <div class="w-11 h-11 bg-profissa-accent rounded-lg text-white flex items-center justify-center text-2xl shadow-accent-glow">
                  <span class="mdi" :class="'mdi-' + form.icon"></span>
                </div>
                <div class="flex-1">
                  <div class="font-bold text-[0.95rem] text-profissa-text-main">{{ selectedIconLabel }}</div>
                  <div class="text-xs font-mono text-profissa-text-dim uppercase tracking-wider">{{ form.icon }}</div>
                </div>
                <el-icon class="text-profissa-text-dim"><ArrowDown /></el-icon>
              </div>
            </template>
            
            <div class="p-0 -m-3 flex flex-col bg-profissa-sidebar-bg">
              <div class="p-4 border-b border-profissa-glass-border">
                <el-input v-model="iconSearch" placeholder="Filtrer les icônes..." clearable :prefix-icon="Search" />
              </div>
              <div class="p-3 max-h-[280px] overflow-y-auto">
                <div class="grid grid-cols-4 gap-2">
                  <button
                    v-for="ic in filteredIcons"
                    :key="ic.mdi"
                    type="button"
                    class="bg-transparent border border-transparent rounded-lg p-3 flex flex-col items-center gap-2 cursor-pointer transition-all hover:bg-white/5 hover:text-profissa-text-main text-profissa-text-muted"
                    :class="{ '!bg-profissa-accent-soft !border-profissa-accent !text-profissa-accent': form.icon === ic.mdi }"
                    @click="form.icon = ic.mdi; iconPopoverVisible = false"
                  >
                    <span class="mdi text-2xl" :class="'mdi-' + ic.mdi"></span>
                    <span class="text-[0.6rem] font-bold uppercase tracking-tighter text-center leading-tight">{{ ic.label }}</span>
                  </button>
                </div>
              </div>
            </div>
          </el-popover>
        </el-form-item>
      </el-form>

      <template #footer>
        <div class="flex justify-end gap-3">
          <el-button @click="closeModal">Annuler</el-button>
          <el-button
            type="primary"
            :loading="isSaving"
            :disabled="!form.label_fr.trim() || !form.key.trim()"
            @click="editingCategory ? saveEdit() : create()"
          >
            {{ editingCategory ? 'Sauvegarder les modifications' : 'Déployer la catégorie' }}
          </el-button>
        </div>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import { Edit, VideoPlay, VideoPause, ArrowDown, Search, Plus, Refresh } from '@element-plus/icons-vue'
import { createCategory, deleteCategory, listCategories, reactivateCategory, updateCategory } from '../services/adminApi'
import { extractApiError } from '../api'
import ActionDropdown from './shared/ActionDropdown.vue'

const ALL_ICONS = [
  { mdi: 'air-conditioner',       label: 'Climatisation'    },
  { mdi: 'wrench',                label: 'Plomberie'        },
  { mdi: 'lightning-bolt',        label: 'Électricité'      },
  { mdi: 'brush',                 label: 'Peinture'         },
  { mdi: 'wall',                  label: 'Maçonnerie'       },
  { mdi: 'saw-blade',             label: 'Menuiserie'       },
  { mdi: 'tree',                  label: 'Jardinage'        },
  { mdi: 'broom',                 label: 'Nettoyage'        },
  { mdi: 'laptop',                label: 'Informatique'     },
  { mdi: 'home',                  label: 'Général'          },
  { mdi: 'key-variant',           label: 'Serrurerie'       },
  { mdi: 'shower',                label: 'Salle de bain'    },
  { mdi: 'power-plug',            label: 'Électroménager'   },
  { mdi: 'window-closed-variant', label: 'Menuiserie alu'   },
  { mdi: 'thermometer',           label: 'Chauffage'        },
  { mdi: 'satellite-variant',     label: 'Antenne / TV'     },
  { mdi: 'sofa',                  label: 'Mobilier'         },
  { mdi: 'bug',                   label: 'Désinsectisation' },
  { mdi: 'crane',                 label: 'Construction'     },
  { mdi: 'car-wrench',            label: 'Mécanique auto'   },
  { mdi: 'water-pump',            label: 'Forage / Pompe'   },
  { mdi: 'bucket',                label: 'Assainissement'   },
  { mdi: 'package-variant',       label: 'Déménagement'     },
  { mdi: 'lock',                  label: 'Sécurité'         },
  { mdi: 'tools',                 label: 'Bricolage'        },
  { mdi: 'pool',                  label: 'Piscine'          },
  { mdi: 'solar-panel',           label: 'Énergie solaire'  },
  { mdi: 'ladder',                label: 'Façade / Toiture' },
  { mdi: 'fire-extinguisher',     label: 'Incendie'         },
  { mdi: 'cctv',                  label: 'Vidéosurveillance'},
]

const selectedRows    = ref([])
const categories      = ref([])
const searchQ         = ref('')
const statusFilter    = ref('')
const showModal       = ref(false)
const iconPopoverVisible = ref(false)
const editingCategory = ref(null)
const form            = ref({ key: '', label_fr: '', icon: 'wrench' })
const iconSearch      = ref('')
const isLoading       = ref(false)
const isSaving        = ref(false)

const filteredCategories = computed(() => {
  let result = categories.value
  if (statusFilter.value === 'active') result = result.filter(c => c.is_active)
  if (statusFilter.value === 'inactive') result = result.filter(c => !c.is_active)
  if (searchQ.value.trim()) {
    const q = searchQ.value.toLowerCase()
    result = result.filter(c => c.label_fr.toLowerCase().includes(q) || c.key.toLowerCase().includes(q))
  }
  return result
})

const filteredIcons = computed(() =>
  iconSearch.value
    ? ALL_ICONS.filter(i => i.label.toLowerCase().includes(iconSearch.value.toLowerCase()) || i.mdi.includes(iconSearch.value.toLowerCase()))
    : ALL_ICONS
)

const onSelectionChange = (rows) => { selectedRows.value = rows }
const bulkLoading = ref(false)

const bulkToggleCategories = async (activate) => {
  const targets = selectedRows.value.filter(c => c.is_active !== activate)
  if (!targets.length) { ElMessage.warning('Aucune catégorie à modifier.'); return }
  try {
    await ElMessageBox.confirm(`${activate ? 'Activer' : 'Désactiver'} ${targets.length} catégorie(s) ?`, 'Action groupée', { type: 'warning' })
    bulkLoading.value = true
    let ok = 0
    for (const c of targets) {
      try {
        if (activate) await reactivateCategory(c.id)
        else await deleteCategory(c.id)
        ok++
      } catch { /* skip */ }
    }
    ElMessage.success(`${ok} catégorie(s) ${activate ? 'activée(s)' : 'désactivée(s)'}.`)
    await fetchCategories()
  } catch { /* cancelled */ }
  finally { bulkLoading.value = false }
}

const selectedIconLabel = computed(() => (ALL_ICONS.find(i => i.mdi === form.value.icon) || ALL_ICONS[0]).label)

const slugify = (s) => s.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '').slice(0, 30)
const autoKey = () => {
  if (!editingCategory.value) {
    form.value.key = slugify(form.value.label_fr)
  }
}

const openCreateModal = () => {
  editingCategory.value = null
  form.value = { key: '', label_fr: '', icon: 'wrench' }
  iconSearch.value = ''; showModal.value = true
}

const openEditModal = (c) => {
  editingCategory.value = c
  form.value = { key: c.key, label_fr: c.label_fr, icon: c.icon || 'wrench' }
  iconSearch.value = ''; showModal.value = true
}

const closeModal = () => {
  showModal.value = false; editingCategory.value = null
}

const fetchCategories = async () => {
  isLoading.value = true
  try { categories.value = await listCategories() }
  catch (e) { ElMessage.error(extractApiError(e)) }
  finally { isLoading.value = false }
}

const getCategoryActions = (row) => [
  { key: 'edit', label: 'Modifier', icon: Edit },
  { 
    key: 'toggle', 
    label: row.is_active ? 'Désactiver' : 'Réactiver', 
    icon: row.is_active ? VideoPause : VideoPlay,
    danger: row.is_active
  },
]

const handleCategoryAction = (key, row) => {
  if (key === 'edit') openEditModal(row)
  if (key === 'toggle') toggle(row)
}

const create = async () => {
  isSaving.value = true
  try {
    await createCategory(form.value)
    ElMessage.success('Catégorie ajoutée avec succès.')
    closeModal(); await fetchCategories()
  } catch (e) { ElMessage.error(extractApiError(e)) }
  finally { isSaving.value = false }
}

const saveEdit = async () => {
  if (!editingCategory.value) return
  isSaving.value = true
  try {
    await updateCategory(editingCategory.value.id, { label_fr: form.value.label_fr, icon: form.value.icon })
    ElMessage.success('Catégorie mise à jour.')
    closeModal(); await fetchCategories()
  } catch (e) { ElMessage.error(extractApiError(e)) }
  finally { isSaving.value = false }
}

const toggle = async (c) => {
  try {
    if (c.is_active) await deleteCategory(c.id)
    else await reactivateCategory(c.id)
    ElMessage.success('Statut de la catégorie mis à jour.')
    await fetchCategories()
  } catch (e) { ElMessage.error(extractApiError(e)) }
}

onMounted(fetchCategories)
</script>

<style scoped>
.page-container { @apply h-full flex flex-col; }
.main-list-card { @apply flex-1 flex flex-col; }
.list-header { @apply flex justify-between items-end pb-8; }
.page-title { @apply text-2xl font-black mb-1; }
.page-subtitle { @apply text-profissa-text-muted text-sm font-semibold; }
.header-actions { @apply flex gap-3 items-center; }

.bulk-bar { @apply flex items-center gap-3 px-4 py-3 mb-4 rounded-xl; background: rgba(255, 107, 0, 0.06); border: 1px solid rgba(255, 107, 0, 0.15); }
.bulk-count { @apply font-extrabold text-sm text-profissa-accent mr-auto; }

/* Force popover internal background for theme consistency */
:deep(.el-popover.premium-icon-popover) {
  @apply p-0 overflow-hidden border-profissa-glass-border !important;
  background-color: var(--sidebar-bg) !important;
}
</style>